/**
 * Environment configuration.
 *
 * Validated once, at startup, and never read from `import.meta.env` anywhere
 * else. A missing API URL should stop the application with a sentence
 * explaining which variable is absent, not surface forty requests later as
 * `fetch("undefined/api/v1/auth/me")`.
 *
 * The data source is deliberately explicit rather than inferred. "Mock unless
 * an API URL happens to be set" is exactly the hidden mixed state that lets a
 * demonstration run half against real data without anybody noticing.
 */

export type DataSource = 'api' | 'mock';

export interface WebEnvironment {
  apiBaseUrl: string;
  wsUrl: string;
  dataSource: DataSource;
  demoMode: boolean;
  realtimeEnabled: boolean;
  uploadMaxBytes: number;
  appVersion: string;
  isProduction: boolean;
}

export class ConfigurationError extends Error {
  constructor(readonly problems: string[]) {
    super(`Configuration is incomplete:\n${problems.map((p) => `  • ${p}`).join('\n')}`);
    this.name = 'ConfigurationError';
  }
}

function readBoolean(value: string | undefined, fallback: boolean): boolean {
  if (value === undefined || value === '') return fallback;
  return value === 'true' || value === '1';
}

export function loadEnvironment(
  raw: Record<string, string | undefined> = import.meta.env as unknown as Record<
    string,
    string | undefined
  >,
): WebEnvironment {
  const problems: string[] = [];

  const dataSource = (raw.VITE_DATA_SOURCE ?? 'mock') as DataSource;
  if (dataSource !== 'api' && dataSource !== 'mock') {
    problems.push(`VITE_DATA_SOURCE must be "api" or "mock" (got "${String(dataSource)}").`);
  }

  const apiBaseUrl = (raw.VITE_API_BASE_URL ?? '').replace(/\/$/, '');
  if (dataSource === 'api' && !apiBaseUrl) {
    problems.push('VITE_API_BASE_URL is required when VITE_DATA_SOURCE is "api".');
  }

  // Vite's PROD is a boolean at build time, a string when injected in tests.
  const isProduction =
    (raw.PROD as unknown) === true || raw.PROD === 'true' || raw.MODE === 'production';

  /*
   * A production build on mock data is almost always a mistake, and it is the
   * kind that reaches a demonstration before anybody spots that the numbers
   * are fictional. Overriding it is possible and has to be deliberate.
   */
  if (isProduction && dataSource === 'mock' && raw.VITE_ALLOW_MOCK_IN_PRODUCTION !== 'true') {
    problems.push(
      'A production build is configured for mock data. Set VITE_DATA_SOURCE=api, or ' +
        'VITE_ALLOW_MOCK_IN_PRODUCTION=true if a prototype build is genuinely intended.',
    );
  }

  const wsUrl = raw.VITE_WS_URL ?? (apiBaseUrl ? `${apiBaseUrl.replace(/^http/, 'ws')}/ws` : '');

  if (problems.length > 0) throw new ConfigurationError(problems);

  return {
    apiBaseUrl,
    wsUrl,
    dataSource,
    demoMode: readBoolean(raw.VITE_DEMO_MODE, !isProduction),
    realtimeEnabled: readBoolean(raw.VITE_REALTIME_ENABLED, dataSource === 'api'),
    uploadMaxBytes: Number(raw.VITE_UPLOAD_MAX_MB ?? 25) * 1024 * 1024,
    appVersion: raw.VITE_APP_VERSION ?? '0.1.0',
    isProduction,
  };
}

let cached: WebEnvironment | null = null;

export function env(): WebEnvironment {
  cached ??= loadEnvironment();
  return cached;
}

/** Test seam. */
export function setEnvironmentForTesting(next: WebEnvironment | null): void {
  cached = next;
}

/** What the development badge shows. Never rendered in production. */
export function dataSourceLabel(environment: WebEnvironment): string {
  if (environment.dataSource === 'mock') return 'Mock Services';
  return environment.demoMode ? 'API With Simulation' : 'Real API';
}
